/**
 * Veredicto del mes en la home del dueño (`/`).
 *
 * Producto: Bien / Atención / Crítico (no jerga interna).
 * Las reglas viven aquí — testeables y documentadas en el PR. No inventar umbrales en el JSX.
 *
 * ## Umbrales (MoM sobre ingresos de caja)
 * - Δ ingresos ≤ −25% → señal crítica
 * - Δ ingresos ≤ −10% → señal de atención
 * - Δ ingresos ≥ −5% (sin otras alertas fuertes) → contribuye a Bien
 *
 * ## Clínicas en alerta (`clinicsInAttention` de retención PVP)
 * - ≥ 50% del total **o** ≥ 3 clínicas → atención; si además hay caída de ingresos ≤ −10% → crítico
 * - ≥ 1 clínica → atención
 *
 * ## Insights críticos (feed de atención)
 * - ≥ 2 → crítico
 * - ≥ 1 → atención
 *
 * ## Resolución
 * Se toma el **peor** nivel entre señales (crítico > atención > bien).
 * Sin periodo anterior no se usa Δ ingresos; igual se evalúan alertas/insights.
 * Si no hay base de comparación ni alertas, el estado es Atención con razón `no_compare`
 * (evitar declarar “Bien” sin datos).
 */

/** Δ ingresos MoM (%) que dispara Atención. */
export const VERDICT_REVENUE_WATCH_PCT = -10;
/** Δ ingresos MoM (%) que dispara Crítico. */
export const VERDICT_REVENUE_CRITICAL_PCT = -25;
/** Δ ingresos MoM (%) por encima del cual se considera estable (contribuye a Bien). */
export const VERDICT_REVENUE_STABLE_FLOOR_PCT = -5;
/** Mínimo de clínicas en alerta (absoluto) para “muchas”. */
export const VERDICT_ALERT_CLINICS_ABS = 3;
/** Fracción de clínicas en alerta para “muchas”. */
export const VERDICT_ALERT_CLINICS_RATIO = 0.5;

export type MonthVerdictStatus = 'good' | 'watch' | 'critical';

export type MonthVerdictReasonCode =
  | 'revenue_critical_drop'
  | 'revenue_watch_drop'
  | 'clinics_alert_many'
  | 'clinics_alert_some'
  | 'insights_critical'
  | 'stable'
  | 'no_compare';

export interface MonthVerdictInput {
  /** Variación MoM de ingresos (%). null si no hay base comparable. */
  revenueDeltaPct: number | null;
  clinicsInAttention: number;
  totalClinics: number;
  criticalInsightCount: number;
  /** false si no hay snapshot del periodo anterior comparable. */
  hasPreviousPeriod: boolean;
}

export interface MonthVerdict {
  status: MonthVerdictStatus;
  /** Primera razón = frase principal; el resto son señales secundarias. */
  reasons: MonthVerdictReasonCode[];
  revenueDeltaPct: number | null;
  alertClinics: number;
  totalClinics: number;
  criticalInsights: number;
}

const STATUS_RANK: Record<MonthVerdictStatus, number> = {
  good: 0,
  watch: 1,
  critical: 2,
};

function worse(a: MonthVerdictStatus, b: MonthVerdictStatus): MonthVerdictStatus {
  return STATUS_RANK[a] >= STATUS_RANK[b] ? a : b;
}

function manyClinicsInAlert(alertClinics: number, totalClinics: number): boolean {
  if (alertClinics <= 0 || totalClinics <= 0) return false;
  if (alertClinics >= VERDICT_ALERT_CLINICS_ABS) return true;
  return alertClinics / totalClinics >= VERDICT_ALERT_CLINICS_RATIO;
}

/**
 * Calcula el veredicto del mes a partir de totales ya cargados en el dashboard.
 */
export function buildMonthVerdict(input: MonthVerdictInput): MonthVerdict {
  const alertClinics = Math.max(0, input.clinicsInAttention);
  const totalClinics = Math.max(0, input.totalClinics);
  const criticalInsights = Math.max(0, input.criticalInsightCount);
  const revenueDeltaPct = input.hasPreviousPeriod ? input.revenueDeltaPct : null;

  const reasons: MonthVerdictReasonCode[] = [];
  let status: MonthVerdictStatus = 'good';

  const revenueCritical =
    revenueDeltaPct !== null && revenueDeltaPct <= VERDICT_REVENUE_CRITICAL_PCT;
  const revenueWatch =
    revenueDeltaPct !== null &&
    revenueDeltaPct <= VERDICT_REVENUE_WATCH_PCT &&
    !revenueCritical;
  const manyAlerts = manyClinicsInAlert(alertClinics, totalClinics);
  const someAlerts = alertClinics >= 1;

  if (revenueCritical) {
    status = worse(status, 'critical');
    reasons.push('revenue_critical_drop');
  } else if (revenueWatch) {
    status = worse(status, 'watch');
    reasons.push('revenue_watch_drop');
  }

  if (manyAlerts) {
    // Muchas clínicas en alerta + caída ≥ watch → crítico; solo muchas → atención.
    if (revenueCritical || revenueWatch) {
      status = worse(status, 'critical');
    } else {
      status = worse(status, 'watch');
    }
    reasons.push('clinics_alert_many');
  } else if (someAlerts) {
    status = worse(status, 'watch');
    reasons.push('clinics_alert_some');
  }

  if (criticalInsights >= 2) {
    status = worse(status, 'critical');
    reasons.push('insights_critical');
  } else if (criticalInsights >= 1) {
    status = worse(status, 'watch');
    reasons.push('insights_critical');
  }

  if (!input.hasPreviousPeriod && reasons.length === 0) {
    status = 'watch';
    reasons.push('no_compare');
  } else if (reasons.length === 0) {
    const stableRevenue =
      revenueDeltaPct === null || revenueDeltaPct >= VERDICT_REVENUE_STABLE_FLOOR_PCT;
    if (stableRevenue) {
      reasons.push('stable');
      status = 'good';
    } else {
      // Caída leve (−10% < Δ < −5%): atención suave.
      status = 'watch';
      reasons.push('revenue_watch_drop');
    }
  }

  return {
    status,
    reasons,
    revenueDeltaPct,
    alertClinics,
    totalClinics,
    criticalInsights,
  };
}

/**
 * Elige la clave i18n de la frase principal (una sola oración de producto).
 */
export function resolveVerdictPhraseKey(reasons: MonthVerdictReasonCode[]): string {
  const hasRevenue = reasons.some((r) => r === 'revenue_critical_drop' || r === 'revenue_watch_drop');
  const hasClinics = reasons.some((r) => r === 'clinics_alert_many' || r === 'clinics_alert_some');
  if (hasRevenue && hasClinics) {
    return reasons.includes('revenue_critical_drop')
      ? 'revenue_critical_and_alerts'
      : 'revenue_watch_and_alerts';
  }
  return reasons[0] ?? 'stable';
}
